import type { SeoLandingContent } from "@/components/SeoLandingTemplate";

/**
 * Content for high-intent SEO landing pages. These pages exist to capture
 * specific buyer search intent (urgent, hard-to-find, supplier queries, etc.)
 * and are intentionally NOT linked from the header, footer, or homepage.
 *
 * Each entry maps 1:1 to a route in App.tsx and a sitemap entry.
 * Add a new entry here, register the route, and rebuild the sitemap to ship a
 * new keyword-targeted landing page.
 */
export const SEO_LANDINGS: Record<string, SeoLandingContent> = {
  "hard-to-find-electrical-components": {
    slug: "hard-to-find-electrical-components",
    breadcrumbLabel: "Hard-to-Find Electrical Components",
    eyebrow: "Hard-to-find sourcing",
    h1: "Hard-to-Find Electrical Components, Sourced Fast",
    heroSub:
      "Discontinued SKUs, allocated parts, obsolete modules — when local distributors say 'no stock,' we locate inventory across a verified U.S. supplier network and deliver to LATAM.",
    metaTitle: "Hard-to-Find Electrical Components | Fast Sourcing",
    metaDescription:
      "Source hard-to-find electrical components fast. Discontinued, allocated, and obsolete SKUs located through a verified U.S. supplier network. Quotes in hours.",
    pain: {
      title: "Why buyers search for hard-to-find components",
      items: [
        "Local distributor shows 'no stock' or quotes 12+ week lead times",
        "OEM has discontinued the SKU but plant still runs on it",
        "Allocated inventory is tied up across the U.S. and Europe",
        "Aftermarket and surplus channels are fragmented and hard to verify",
        "Project deadline can't absorb a 3-month wait",
      ],
    },
    provide: {
      title: "What we provide",
      items: [
        {
          title: "Cross-supplier search",
          body: "We query multiple verified U.S. suppliers per request — not a single distributor inventory.",
        },
        {
          title: "Real availability",
          body: "We confirm stock and lead time before quoting — no placeholder dates.",
        },
        {
          title: "Firm landed pricing",
          body: "Product, freight, duties, and final-mile delivery calculated upfront (DDP).",
        },
        {
          title: "Delivery coordination",
          body: "We handle export, customs, and final-mile delivery across LATAM end-to-end.",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh",
      items: [
        {
          title: "Built for the hard ones",
          body: "We don't compete on commodity stock. We focus on parts that local channels can't access.",
        },
        {
          title: "Hours, not days",
          body: "Most quotes come back within hours, including availability and a firm landed price.",
        },
        {
          title: "Verified U.S. network",
          body: "Suppliers are vetted for stock accuracy, fulfillment reliability, and compliance.",
        },
        {
          title: "Single point of contact",
          body: "One sourcing manager owns your request from quote through delivery.",
        },
      ],
    },
    expectations: {
      title: "What to expect",
      items: [
        {
          title: "Submit a SKU, photo, or spec",
          body: "A part number gets the fastest, most accurate quote. Photos and spec sheets also work.",
        },
        {
          title: "Availability confirmed in hours",
          body: "We confirm stock with verified suppliers before quoting — no speculative pricing.",
        },
        {
          title: "Firm landed price",
          body: "Quote includes product, freight, duties, taxes, and final-mile delivery (DDP).",
        },
        {
          title: "Delivery in days, not months",
          body: "Typical delivery to major LATAM destinations runs in days for in-stock items.",
        },
      ],
    },
    finalCta: {
      title: "Send the part number — get availability in hours",
      body: "Submit your hard-to-find component and we'll come back with confirmed stock and landed pricing.",
    },
  },

  "urgent-electrical-components": {
    slug: "urgent-electrical-components",
    breadcrumbLabel: "Urgent Electrical Components",
    eyebrow: "Urgent sourcing",
    h1: "Urgent Electrical Components — Quoted in Hours",
    heroSub:
      "Production line down, project at risk, or replacement part needed this week? We respond in hours with confirmed availability and firm landed pricing across LATAM.",
    metaTitle: "Urgent Electrical Components Supplier | Quotes in Hours",
    metaDescription:
      "Urgent electrical components supplier for industrial buyers. Confirmed availability and firm landed pricing in hours. Delivered across LATAM in days.",
    pain: {
      title: "When every hour costs money",
      items: [
        "Production line down — every hour of downtime burns revenue",
        "Critical replacement breaker or contactor is out of local stock",
        "Project commissioning blocked by a missing PLC module or drive",
        "Local quote turnaround is 3–5 days and you don't have 3–5 days",
        "Need a confirmed delivery date, not a 'maybe in 8 weeks'",
      ],
    },
    provide: {
      title: "What we provide",
      items: [
        {
          title: "Same-day response",
          body: "Urgent requests are flagged and routed to a sourcing manager immediately.",
        },
        {
          title: "Confirmed availability",
          body: "We don't quote what we can't ship. Stock is verified before pricing goes out.",
        },
        {
          title: "Expedited freight",
          body: "Air freight options included where transit time is the constraint.",
        },
        {
          title: "DDP delivery",
          body: "We handle export docs, duties, and final-mile so the part lands on your dock.",
        },
      ],
    },
    whyUs: {
      title: "Why Switzh for urgent sourcing",
      items: [
        {
          title: "Operational, not transactional",
          body: "We're built around urgent procurement — not a generic distributor catalog.",
        },
        {
          title: "Multiple suppliers, one request",
          body: "We query the network in parallel so you get the fastest available stock, not the first hit.",
        },
        {
          title: "WhatsApp + email",
          body: "Send a SKU, a photo, or a spec via WhatsApp. We respond in the same channel.",
        },
        {
          title: "LATAM execution",
          body: "We deliver to Mexico, Colombia, Peru, Chile, Brazil, and Argentina with predictable transit.",
        },
      ],
    },
    expectations: {
      title: "Urgent request flow",
      items: [
        {
          title: "Send the request",
          body: "Part number + destination is enough to start. WhatsApp is fastest for urgent jobs.",
        },
        {
          title: "Availability in hours",
          body: "We come back with verified stock, lead time, and freight options.",
        },
        {
          title: "Firm landed price",
          body: "Quote includes product, expedited freight, duties, and final-mile delivery.",
        },
        {
          title: "Pickup and ship",
          body: "Once approved, we coordinate pickup, export, and customs to your destination.",
        },
      ],
    },
    finalCta: {
      title: "Send your urgent request now",
      body: "Confirmed availability and firm landed pricing within hours — not days.",
    },
  },

  "electrical-components-supplier": {
    slug: "electrical-components-supplier",
    breadcrumbLabel: "Electrical Components Supplier",
    eyebrow: "Industrial supplier",
    h1: "Electrical Components Supplier for Industrial Buyers",
    heroSub:
      "A single sourcing partner across breakers, contactors, PLCs, drives, and switchgear — backed by a verified U.S. supplier network and DDP delivery to LATAM.",
    metaTitle: "Electrical Components Supplier — Industrial Sourcing Partner",
    metaDescription:
      "Electrical components supplier for EPCs, contractors, and plant operators. Verified U.S. network, firm landed pricing, DDP delivery across LATAM.",
    pain: {
      title: "Why buyers consolidate sourcing with us",
      items: [
        "Managing 10+ distributor relationships per project",
        "Inconsistent quote turnaround and pricing transparency",
        "Local stock shortages on common SKUs across multiple brands",
        "No single owner for cross-border freight, duties, and final-mile",
        "Need one supplier accountable from quote to delivered part",
      ],
    },
    provide: {
      title: "What we supply",
      items: [
        {
          title: "Circuit protection",
          body: "MCCBs, ACBs, contactors, overload relays from major brands.",
        },
        {
          title: "Automation & control",
          body: "PLC modules, I/O cards, HMIs, control components, sensors.",
        },
        {
          title: "Power & distribution",
          body: "Transformers, switchgear components, power supplies, drives.",
        },
        {
          title: "Pre-configured packages",
          body: "Built-to-spec power kits when you need a system, not just a SKU.",
        },
      ],
    },
    categories: {
      title: "Browse by component family",
      items: [
        { name: "MCCB breakers", href: "/fast-access-power-components/mccb-breakers" },
        { name: "PLC modules", href: "/fast-access-power-components/plc-modules" },
        { name: "Contactors & overloads", href: "/fast-access-power-components/contactors-overloads" },
        { name: "VFD drives", href: "/fast-access-power-components/vfd-drives" },
        { name: "Industrial power supplies", href: "/fast-access-power-components/industrial-power-supplies" },
        { name: "Industrial sensors", href: "/fast-access-power-components/industrial-sensors" },
      ],
    },
    whyUs: {
      title: "Why work with us as your supplier",
      items: [
        {
          title: "One contact, full ownership",
          body: "A single sourcing manager owns your account end-to-end.",
        },
        {
          title: "Verified U.S. supplier base",
          body: "Pre-vetted distributors and aftermarket sources — not random surplus.",
        },
        {
          title: "Firm landed pricing (DDP)",
          body: "Product, freight, duties, and delivery in one quote. No border surprises.",
        },
        {
          title: "EN · ES · PT support",
          body: "We work in your language across the LATAM and U.S. timezones.",
        },
      ],
    },
    expectations: {
      title: "Working with us",
      items: [
        {
          title: "Open an account or send an RFQ",
          body: "No onboarding fee. Send a SKU list or project BOM to start.",
        },
        {
          title: "Receive quotes in hours",
          body: "Most line items come back same-day with confirmed availability.",
        },
        {
          title: "Approve and ship",
          body: "We handle freight, customs, and final-mile to your destination.",
        },
        {
          title: "Reorder with one message",
          body: "Repeat orders move faster — your supplier history stays with us.",
        },
      ],
    },
    finalCta: {
      title: "Make us your electrical components supplier",
      body: "Send an RFQ — we'll come back with availability, firm landed pricing, and a delivery date.",
    },
  },

  "industrial-electrical-components": {
    slug: "industrial-electrical-components",
    breadcrumbLabel: "Industrial Electrical Components",
    eyebrow: "Industrial sourcing",
    h1: "Industrial Electrical Components for Plants & EPCs",
    heroSub:
      "Standardized industrial electrical components — circuit protection, automation, drives, and distribution — sourced from verified U.S. suppliers and delivered to LATAM with full landed cost.",
    metaTitle: "Industrial Electrical Components | Sourcing & Supply",
    metaDescription:
      "Industrial electrical components for plants, EPCs, and contractors. MCCBs, PLCs, drives, and switchgear sourced fast with DDP delivery across LATAM.",
    pain: {
      title: "What industrial buyers need from a sourcing partner",
      items: [
        "Reliable supply on widely used SKUs across major brands",
        "Firm landed cost — not 'EXW + you figure out the rest'",
        "Sourcing managers who understand industrial spec language",
        "Single accountable supplier across the U.S. → LATAM corridor",
        "Predictable transit times for project planning",
      ],
    },
    provide: {
      title: "What we cover",
      items: [
        {
          title: "Circuit protection",
          body: "MCCBs, ACBs, contactors, overload relays — major brands, multiple frame sizes.",
        },
        {
          title: "Automation & control",
          body: "PLC modules, I/O cards, HMIs, control relays, industrial sensors.",
        },
        {
          title: "Drives & motor control",
          body: "VFDs, soft starters, and motor protection devices for industrial loads.",
        },
        {
          title: "Power distribution",
          body: "Transformers, panel components, power supplies, switchgear parts.",
        },
      ],
    },
    whyUs: {
      title: "Built for industrial procurement",
      items: [
        {
          title: "Spec-driven sourcing",
          body: "We validate frame size, voltage, current, and trip unit before quoting.",
        },
        {
          title: "Multi-brand coverage",
          body: "ABB, Siemens, Schneider, Eaton, Allen-Bradley, and more from a single RFQ.",
        },
        {
          title: "Verified U.S. supply chain",
          body: "Distributors and aftermarket sources vetted for accuracy and compliance.",
        },
        {
          title: "DDP to LATAM",
          body: "We handle export, customs, and final-mile delivery as one landed cost.",
        },
      ],
    },
    expectations: {
      title: "How a typical sourcing run works",
      items: [
        {
          title: "Send the BOM or part list",
          body: "SKUs, quantities, and destination are enough to start. We'll flag any spec gaps.",
        },
        {
          title: "Quote returned in hours",
          body: "Each line includes confirmed availability, lead time, and landed price.",
        },
        {
          title: "Approve and ship",
          body: "We coordinate pickup, export, customs, and final-mile delivery to your site.",
        },
        {
          title: "Track to delivery",
          body: "Order status and tracking are shared throughout the shipment lifecycle.",
        },
      ],
    },
    finalCta: {
      title: "Send your BOM — get a landed quote in hours",
      body: "We source industrial electrical components across major brands and deliver DDP across LATAM.",
    },
  },

  "electrical-procurement-services": {
    slug: "electrical-procurement-services",
    breadcrumbLabel: "Electrical Procurement Services",
    eyebrow: "Procurement services",
    h1: "Electrical Procurement Services — From SKU to Delivered",
    heroSub:
      "End-to-end procurement for industrial electrical components: sourcing, supplier vetting, landed pricing, export, customs, and final-mile delivery — owned by a single team.",
    metaTitle: "Electrical Procurement Services for Industrial Buyers",
    metaDescription:
      "Electrical procurement services covering sourcing, landed pricing, export, customs, and DDP delivery to LATAM. One accountable team from RFQ to delivered.",
    pain: {
      title: "What's broken in traditional procurement",
      items: [
        "Quotes scattered across 5–10 distributors per project",
        "Freight, duties, and final-mile handled by separate vendors",
        "No accountability when a shipment misses the date",
        "Hidden costs added at the border after PO is issued",
        "Procurement teams stretched across hundreds of line items",
      ],
    },
    provide: {
      title: "What our procurement service covers",
      items: [
        {
          title: "Sourcing & supplier vetting",
          body: "We identify verified U.S. suppliers per SKU and validate stock before quoting.",
        },
        {
          title: "Landed cost calculation",
          body: "Product, freight, duties, taxes, and final-mile in one transparent quote.",
        },
        {
          title: "Export & customs",
          body: "We handle commercial invoices, HS codes, and customs clearance end-to-end.",
        },
        {
          title: "Final-mile delivery",
          body: "Coordinated delivery to plant, warehouse, or job site across LATAM.",
        },
      ],
    },
    whyUs: {
      title: "Why buyers outsource procurement to us",
      items: [
        {
          title: "One team, full lifecycle",
          body: "RFQ to delivered — same sourcing manager, same accountability.",
        },
        {
          title: "Volume leverage",
          body: "Aggregated demand across our network gets you better pricing per SKU.",
        },
        {
          title: "Compliance built in",
          body: "Export controls, HS classification, and country-specific docs handled.",
        },
        {
          title: "Faster cycle times",
          body: "Hours for quotes, days for delivery on in-stock items.",
        },
      ],
    },
    expectations: {
      title: "How engagement works",
      items: [
        {
          title: "Send a project BOM or single SKU",
          body: "We work at any scale — single replacement parts to multi-line industrial BOMs.",
        },
        {
          title: "Receive a landed quote",
          body: "Each line includes verified stock, lead time, and DDP pricing.",
        },
        {
          title: "Approve and we execute",
          body: "We handle pickup, export, customs, and final-mile delivery.",
        },
        {
          title: "Receive and reorder",
          body: "Tracking shared through delivery; reorders move faster on file.",
        },
      ],
    },
    finalCta: {
      title: "Outsource the procurement, keep the timeline",
      body: "Send your BOM and we'll come back with a landed quote — sourcing through delivery owned by us.",
    },
  },

  "global-electrical-sourcing": {
    slug: "global-electrical-sourcing",
    breadcrumbLabel: "Global Electrical Sourcing",
    eyebrow: "Global sourcing",
    h1: "Global Electrical Sourcing for LATAM Industrial Buyers",
    heroSub:
      "Access fragmented global supply through a single sourcing layer. We locate inventory across the U.S. and verified channels, then deliver to LATAM with full landed cost.",
    metaTitle: "Global Electrical Sourcing — U.S. Supply to LATAM",
    metaDescription:
      "Global electrical sourcing for LATAM industrial buyers. Verified U.S. supplier network, firm landed pricing, and DDP delivery to Mexico, Colombia, Peru, and more.",
    pain: {
      title: "Why local sourcing isn't enough",
      items: [
        "Local distributors carry a fraction of available global supply",
        "Same SKU often has 30–60% price variance across the U.S. market",
        "Allocated inventory in Europe is invisible to local channels",
        "Cross-border procurement is operationally heavy without a partner",
        "Single-supplier dependency creates schedule risk",
      ],
    },
    provide: {
      title: "What our global sourcing layer provides",
      items: [
        {
          title: "Verified U.S. network",
          body: "Distributors and aftermarket sources pre-vetted for stock accuracy.",
        },
        {
          title: "Multi-source comparison",
          body: "We query the network in parallel and surface the best stock + price combo.",
        },
        {
          title: "Cross-border execution",
          body: "Export, customs, and DDP delivery handled end-to-end.",
        },
        {
          title: "Single point of contact",
          body: "One sourcing manager owns your account regardless of supplier mix.",
        },
      ],
    },
    whyUs: {
      title: "Why use a global sourcing layer",
      items: [
        {
          title: "Access, not just inventory",
          body: "We surface availability your local channels can't see.",
        },
        {
          title: "Price arbitrage",
          body: "Same SKU, lower landed cost when supply chain math works in your favor.",
        },
        {
          title: "Risk diversification",
          body: "Multiple supplier paths per request reduce single-source schedule risk.",
        },
        {
          title: "Built for LATAM destinations",
          body: "Customs, duties, and final-mile handled across the major LATAM markets.",
        },
      ],
    },
    expectations: {
      title: "How it works",
      items: [
        {
          title: "Submit your request",
          body: "SKU list, BOM, or spec sheet — destination country and timing.",
        },
        {
          title: "Multi-source quote",
          body: "We come back with verified stock, lead time, and landed cost per line.",
        },
        {
          title: "Approve and ship",
          body: "Pickup, export, customs, and final-mile coordinated by us.",
        },
        {
          title: "Delivered DDP",
          body: "Part lands at your dock with no border surprises.",
        },
      ],
    },
    finalCta: {
      title: "Tap into global supply through one sourcing layer",
      body: "Send your request and we'll surface real availability across our verified U.S. network.",
    },
  },

  "power-distribution-equipment-supplier": {
    slug: "power-distribution-equipment-supplier",
    breadcrumbLabel: "Power Distribution Equipment Supplier",
    eyebrow: "Power distribution",
    h1: "Power Distribution Equipment Supplier — Components & Systems",
    heroSub:
      "Switchgear, transformers, panel components, ATS systems, and distribution boards — sourced from verified U.S. suppliers and delivered DDP across LATAM.",
    metaTitle: "Power Distribution Equipment Supplier | LATAM Delivery",
    metaDescription:
      "Power distribution equipment supplier for industrial sites. Switchgear, transformers, ATS, and panel components. Firm landed pricing and DDP delivery to LATAM.",
    pain: {
      title: "What plant operators and EPCs need",
      items: [
        "Distribution gear that's available now, not in 16 weeks",
        "Single supplier across switchgear, transformers, and ATS",
        "Firm landed cost for project budget commitments",
        "Predictable freight on heavy and oversized equipment",
        "DDP delivery to remote industrial sites",
      ],
    },
    provide: {
      title: "What we supply",
      items: [
        {
          title: "LV & MV switchgear components",
          body: "Breakers, busbars, panel components, and switchgear spares.",
        },
        {
          title: "Transformers",
          body: "Distribution and power transformers across major brands and ratings.",
        },
        {
          title: "ATS & transfer systems",
          body: "Automatic transfer switches and bypass equipment for critical loads.",
        },
        {
          title: "Panel & board components",
          body: "Distribution boards, sub-panels, and panel-mount components.",
        },
      ],
    },
    whyUs: {
      title: "Why source distribution equipment with us",
      items: [
        {
          title: "Single supplier across the gear",
          body: "Breakers, transformers, ATS, and panel parts on one PO.",
        },
        {
          title: "Heavy-freight expertise",
          body: "We coordinate freight on large transformers and switchgear sections.",
        },
        {
          title: "DDP across LATAM",
          body: "Delivered to plant or job site with all duties and taxes settled.",
        },
        {
          title: "Spec validation",
          body: "We confirm voltage, kVA, frame size, and config before quoting.",
        },
      ],
    },
    expectations: {
      title: "Engagement flow",
      items: [
        {
          title: "Send the spec or SLD",
          body: "Single-line diagram, transformer spec, or switchgear schedule works.",
        },
        {
          title: "Quote in hours to days",
          body: "Standard distribution gear comes back fast; engineered equipment runs days.",
        },
        {
          title: "Approve and we ship",
          body: "Heavy freight coordinated end-to-end with customs and final-mile.",
        },
        {
          title: "Delivered to site",
          body: "Coordinated unloading and delivery to industrial destinations.",
        },
      ],
    },
    finalCta: {
      title: "Source your distribution equipment in one place",
      body: "Send your spec or SLD — we'll come back with a landed quote and a delivery date.",
    },
  },

  "mccb-breaker-supplier": {
    slug: "mccb-breaker-supplier",
    breadcrumbLabel: "MCCB Breaker Supplier",
    eyebrow: "MCCB sourcing",
    h1: "MCCB Breaker Supplier — Major Brands, Fast Sourcing",
    heroSub:
      "Molded case circuit breakers from ABB, Siemens, Schneider, Eaton, and more. Single SKU or full panel — sourced from verified U.S. suppliers and delivered DDP to LATAM.",
    metaTitle: "MCCB Breaker Supplier | ABB Siemens Schneider Eaton",
    metaDescription:
      "MCCB breaker supplier covering ABB, Siemens, Schneider, and Eaton. Firm landed pricing, verified U.S. stock, and DDP delivery to LATAM in days.",
    pain: {
      title: "Why buyers source MCCBs through us",
      items: [
        "Local distributor only carries one brand or frame size",
        "Replacement breaker for legacy panel is on long lead time",
        "Project requires multi-brand MCCBs from a single PO",
        "Need confirmed stock before issuing the purchase order",
        "Cross-border freight and duties not handled by local supplier",
      ],
    },
    provide: {
      title: "What we cover",
      items: [
        {
          title: "All major brands",
          body: "ABB Tmax, Siemens 3VA, Schneider NSX, Eaton — across frame sizes.",
        },
        {
          title: "Trip units & accessories",
          body: "Electronic and thermal-magnetic trip units, shunt trips, aux contacts.",
        },
        {
          title: "Equivalents on request",
          body: "We can suggest verified equivalents when an exact SKU is constrained.",
        },
        {
          title: "Volume + replacement",
          body: "Single replacement breakers or full panel quantities — same process.",
        },
      ],
    },
    categories: {
      title: "Browse MCCB pages",
      items: [
        { name: "MCCB breakers collection", href: "/fast-access-power-components/mccb-breakers" },
        { name: "ABB Tmax T5", href: "/product/abb-tmax-t5" },
        { name: "Schneider NSX (Mexico)", href: "/blog/schneider-nsx-mexico-2026" },
        { name: "ABB breakers (LATAM)", href: "/blog/abb-breakers-availability-latam-2026" },
      ],
    },
    whyUs: {
      title: "Why source MCCBs with Switzh",
      items: [
        {
          title: "Multi-brand, single quote",
          body: "Mix ABB, Siemens, Schneider, and Eaton on one RFQ.",
        },
        {
          title: "Verified U.S. stock",
          body: "We confirm availability with the supplier before quoting.",
        },
        {
          title: "Firm landed pricing",
          body: "DDP — product, freight, duties, and final-mile in one number.",
        },
        {
          title: "LATAM delivery in days",
          body: "Standard MCCBs ship to major LATAM markets in days, not weeks.",
        },
      ],
    },
    expectations: {
      title: "How to request a quote",
      items: [
        {
          title: "Send the SKU or breaker spec",
          body: "Frame size, current, poles, trip unit type — or a part number.",
        },
        {
          title: "Availability + price in hours",
          body: "We come back with confirmed stock and DDP landed price per line.",
        },
        {
          title: "Approve and ship",
          body: "We coordinate freight, customs, and delivery to your site.",
        },
        {
          title: "Reorder anytime",
          body: "Repeat MCCB orders move faster — your spec history stays on file.",
        },
      ],
    },
    finalCta: {
      title: "Get an MCCB quote in hours",
      body: "Send the SKU or breaker spec — we'll come back with stock and a landed price.",
    },
  },

  "plc-modules-supplier": {
    slug: "plc-modules-supplier",
    breadcrumbLabel: "PLC Modules Supplier",
    eyebrow: "PLC sourcing",
    h1: "PLC Modules Supplier — Siemens, Allen-Bradley, Schneider",
    heroSub:
      "Specific PLC modules, I/O cards, communication modules, and HMIs sourced fast from verified U.S. suppliers. Single SKU replacement to multi-rack BOMs.",
    metaTitle: "PLC Modules Supplier | Siemens Allen-Bradley Schneider",
    metaDescription:
      "PLC modules supplier covering Siemens S7, Allen-Bradley ControlLogix, and Schneider Modicon. Verified U.S. stock and DDP delivery to LATAM in days.",
    pain: {
      title: "Why PLC sourcing is hard",
      items: [
        "Specific module SKUs are allocated globally and hard to find local",
        "Legacy PLC platforms (S7-300, SLC-500) need aftermarket sourcing",
        "Firmware revision matters — generic 'in stock' isn't enough",
        "Project commissioning blocked by one missing I/O card",
        "Local quote turnaround is too slow for a downtime event",
      ],
    },
    provide: {
      title: "What we source",
      items: [
        {
          title: "Siemens S7",
          body: "S7-1200, S7-1500, and aftermarket S7-300/400 modules.",
        },
        {
          title: "Allen-Bradley",
          body: "ControlLogix, CompactLogix, and legacy SLC-500 / PLC-5 modules.",
        },
        {
          title: "Schneider Modicon",
          body: "M340, M580, and Quantum platform modules and I/O cards.",
        },
        {
          title: "Comm & HMI",
          body: "Communication modules, HMIs, and panel PCs from major brands.",
        },
      ],
    },
    categories: {
      title: "Related pages",
      items: [
        { name: "PLC modules collection", href: "/fast-access-power-components/plc-modules" },
        { name: "Siemens S7 (Chile mining)", href: "/blog/siemens-s7-chile-mining-2026" },
        { name: "PLC suppliers (Peru mining)", href: "/blog/plc-suppliers-peru-mining-2026" },
        { name: "PLCs (Colombia oil & gas)", href: "/blog/plcs-colombia-oil-gas-2026" },
      ],
    },
    whyUs: {
      title: "Why source PLCs with us",
      items: [
        {
          title: "SKU-level accuracy",
          body: "We confirm exact part number and firmware before quoting.",
        },
        {
          title: "Aftermarket + new",
          body: "We can source legacy modules from verified aftermarket channels.",
        },
        {
          title: "Multi-brand RFQs",
          body: "Mix Siemens, AB, and Schneider on the same purchase order.",
        },
        {
          title: "DDP to LATAM",
          body: "Customs, duties, and final-mile delivery handled by us.",
        },
      ],
    },
    expectations: {
      title: "How to request a PLC module",
      items: [
        {
          title: "Send the part number",
          body: "Exact SKU + firmware revision (if relevant) gets the most accurate quote.",
        },
        {
          title: "Stock confirmed in hours",
          body: "We verify availability and lead time before pricing goes out.",
        },
        {
          title: "Firm landed price",
          body: "Product, freight, duties, and delivery — one number, no surprises.",
        },
        {
          title: "Delivery in days",
          body: "In-stock modules ship to major LATAM destinations in days.",
        },
      ],
    },
    finalCta: {
      title: "Send the PLC part number — quote in hours",
      body: "We source Siemens, Allen-Bradley, and Schneider modules from verified U.S. suppliers.",
    },
  },

  "ats-transfer-switch-supplier": {
    slug: "ats-transfer-switch-supplier",
    breadcrumbLabel: "ATS Transfer Switch Supplier",
    eyebrow: "ATS sourcing",
    h1: "ATS Transfer Switch Supplier — Critical Power Continuity",
    heroSub:
      "Automatic transfer switches and bypass-isolation equipment for data centers, hospitals, and industrial sites. Sourced fast and delivered DDP across LATAM.",
    metaTitle: "ATS Transfer Switch Supplier | Critical Power Continuity",
    metaDescription:
      "ATS transfer switch supplier for critical power applications. ASCO, Cummins, Eaton, and Generac equipment. Firm landed pricing and DDP delivery to LATAM.",
    pain: {
      title: "Why buyers source ATS through us",
      items: [
        "ATS replacement urgent after a critical power event",
        "New build needs ATS rated for specific kW and transition type",
        "Local supplier doesn't carry the brand specified by the engineer",
        "Bypass-isolation switches require coordination with switchgear",
        "Long OEM lead times don't fit project commissioning schedule",
      ],
    },
    provide: {
      title: "What we cover",
      items: [
        {
          title: "Major ATS brands",
          body: "ASCO, Cummins, Eaton, Generac, Kohler, and Russelectric.",
        },
        {
          title: "Open & closed transition",
          body: "Open transition (break-before-make) and closed transition ATS.",
        },
        {
          title: "Bypass-isolation",
          body: "Bypass-isolation ATS for service-without-shutdown applications.",
        },
        {
          title: "Service entrance rated",
          body: "SE-rated ATS for code-compliant service entrance applications.",
        },
      ],
    },
    whyUs: {
      title: "Why source ATS with us",
      items: [
        {
          title: "Critical-power focus",
          body: "We understand transition types, ratings, and code requirements.",
        },
        {
          title: "Brand flexibility",
          body: "We source the brand the engineer specified — not just what's local.",
        },
        {
          title: "Heavy-freight handling",
          body: "We coordinate freight on large ATS units to LATAM destinations.",
        },
        {
          title: "DDP delivery",
          body: "Customs, duties, and final-mile handled — one landed price.",
        },
      ],
    },
    expectations: {
      title: "How to request an ATS",
      items: [
        {
          title: "Send the spec or SKU",
          body: "Brand, amperage, voltage, transition type — or a model number.",
        },
        {
          title: "Quote in hours to days",
          body: "Standard models come back fast; engineered configs may run days.",
        },
        {
          title: "Approve and we ship",
          body: "We coordinate freight, customs, and delivery to your site.",
        },
        {
          title: "Delivered DDP",
          body: "ATS lands at the site with all duties and taxes settled.",
        },
      ],
    },
    finalCta: {
      title: "Source your ATS through one supplier",
      body: "Send the spec or SKU — we'll come back with availability and a firm landed price.",
    },
  },
};

/** Stable slug list for routing + sitemap generation. */
export const SEO_LANDING_SLUGS = Object.keys(SEO_LANDINGS);